import { SLICE_TAG } from '@/constants';

import { apiSlice } from '../api-slice';
import {
    CartList,
    ResponseCartListSchema,
    ResponseDraftListSchema,
} from './cart.schema';

export const cartApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        addToCart: builder.mutation({
            query: (data) => ({
                url: 'cart',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.CART_SALE, SLICE_TAG.INVENTORY_SALE],
        }),

        addToDraft: builder.mutation({
            query: (data) => ({
                url: 'cart/drafts',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.CART_SALE],
        }),

        draftToCart: builder.query({
            query: (id) => {
                return {
                    url: `cart/drafts/${id}/active`,
                };
            },
            // providesTags: [''],
            keepUnusedDataFor: 600,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        apiSlice.util.invalidateTags([
                            'Cart_Sale',
                            'Inventory_Sale',
                        ]),
                    );
                } catch (err) {
                    console.error('Failed to draft to cart', err);
                }
            },
        }),

        getAllDrafts: builder.query({
            query: () => 'cart/drafts',
            providesTags: [SLICE_TAG.CART_SALE],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return ResponseDraftListSchema.parse(response);
            },
        }),

        removeSingleCartItem: builder.mutation({
            query: (id) => ({
                url: `cart/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [SLICE_TAG.CART_SALE, SLICE_TAG.INVENTORY_SALE],
        }),

        clearCart: builder.query<any, any>({
            query: () => 'cart/clear',
            providesTags: [SLICE_TAG.CART_CLEAR],
            keepUnusedDataFor: 600,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        apiSlice.util.invalidateTags([
                            SLICE_TAG.CART_SALE,
                            SLICE_TAG.INVENTORY_SALE,
                        ]),
                    );
                } catch (err) {
                    console.error('Failed to clear cart', err);
                }
            },
        }),

        confirmOrderMultiPayment: builder.mutation<
            any,
            {
                payments: {
                    amount: number;
                    transaction_id: string;
                    payment_method: string;
                }[];
            }
        >({
            query: (data) => ({
                url: 'orders/confirm-multi-payment',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.CART_SALE, SLICE_TAG.SALES],
        }),

        // getCartProductForSale and getActiveCart are same
        // This one is kept for legacy purposes
        // to avoid breaking changes
        getCartProductForSale: builder.query<CartList, string>({
            query: () => 'cart',
            providesTags: [SLICE_TAG.CART_SALE],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return ResponseCartListSchema.parse(response);
            },
        }),

        getActiveCart: builder.query<CartList, string>({
            query: () => 'cart',
            providesTags: [SLICE_TAG.CART_SALE],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return ResponseCartListSchema.parse(response);
            },
        }),

        updateCart: builder.mutation({
            query: (data) => ({
                url: `cart/update-cart`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [SLICE_TAG.CART_SALE],
        }),

        updateCartById: builder.mutation({
            query: (data) => {
                const { id, ...rest } = data;
                return {
                    url: `cart/${id}`,
                    method: 'PUT',
                    body: rest,
                };
            },
            invalidatesTags: [SLICE_TAG.CART_SALE],
        }),
    }),
});

export const {
    useAddToCartMutation,
    useGetCartProductForSaleQuery,
    useLazyClearCartQuery,
    useRemoveSingleCartItemMutation,
    useUpdateCartMutation,
    useUpdateCartByIdMutation,
    useAddToDraftMutation,
    useLazyDraftToCartQuery,
    useGetAllDraftsQuery,
    useClearCartQuery,
    useDraftToCartQuery,
    useLazyGetAllDraftsQuery,
    useLazyGetCartProductForSaleQuery,
    useGetActiveCartQuery,
    useLazyGetActiveCartQuery,
    useConfirmOrderMultiPaymentMutation,
} = cartApi;
